import { useContext } from 'react'

import { formatMoney } from 'accounting'

import {
  Box, Button, Form, ModalContext, SidebarModal, SidebarNoticeBox, Text,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useCampaign from '@hooks/useCampaign'
import useCreditNote, { useCreditNoteForm } from '@hooks/useCreditNote'

import { confirmAction, confirmDelete } from '@functions/entity'

const MODAL_KEY = 'CreateCreditNoteAndDeleteOrderModal'

type ModalCallbacks = {
  closeModal: () => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateCreditNoteAndDeleteOrderModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { entity: initEntity, deleteFn } = modalPayload

  const {
    campaign_id, id, price_override, total, type,
  } = initEntity

  const formattedType = type === 'Order' ? 'Order' : 'Custom Item'

  const { campaign } = useCampaign({ id: campaign_id })

  const actualTotal = total || price_override

  const creditNotePayload = useCreditNote({
    campaign_id,
    creditor_id: campaign?.agency_id,
    creditor_type: 'Agency',
  })

  const {
    callbacks: { createCreditNote: createCreditNoteFn },
    creating,
    creditNote,
  } = creditNotePayload

  const creditNoteFormPayload = useCreditNoteForm(creditNote, {
    validateOn: 'change',
    customRequiredFields: [{ key: 'notes' }],
  })

  const {
    entityState,
    entityState: {
      notes,
    },
    errors,
    handlers,
    saveEnabled,
  } = creditNoteFormPayload

  const createCreditNotePayload = {
    callbacks: {
      actionFn: createCreditNoteFn,
      afterAction: () => {
        deleteFn()
        closeModal()
      },
    },
    options: {
      confirmButtonColor: '#DD6B55',
      entityParams: {
        ...entityState,
        campaign_item_id: type === 'CampaignItem' ? id : null,
        order_id: type === 'Order' ? id : null,
      },
      icon: 'warning',
    },
    strings: {
      actionString: 'Delete',
      confirmString: 'Are you sure? This is not reversable. A Credit Note will be created to account for the deleted item.',
      entityName: `${formattedType}`,
    },
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header
        callbacks={callbacks}
        title="Create"
        titleSecondLine="Credit Note"
      />

      <SidebarModal.Content>
        <Box flexDirection="column">
          <Form.Field label="Credit Note">
            <Box
              alignItems="center"
              flexDirection="column"
              justifyContent="center"
              padding="large"
              variant="whiteGrey"
            >
              <Text fontSize="large">{actualTotal ? formatMoney(actualTotal) : '$0.00'}</Text>

              <Text color="bodyFontLightColor" fontSize="xsmall" marginTop="small">
                {formattedType} #{id}
              </Text>
            </Box>
          </Form.Field>

          <Form.Field label="* Authorisation / Reason" marginTop="large" errorMessage={errors.notes}>
            {/*
             // @ts-expect-error TS2322 */}
            <textarea name="notes" value={notes} style={{ height: 160, resize: 'vertical' }} {...handlers} />
          </Form.Field>

          <SidebarNoticeBox boxProps={{ marginTop: 'large' }}>
            <SidebarNoticeBox.Title>Why am I seeing this?</SidebarNoticeBox.Title>

            <SidebarNoticeBox.Paragraph>
              This item has been created from an approved quote. Because the spending has been authorised this item has
              a corresponding invoice. A credit note needs to be created so that the campaign will balance.
            </SidebarNoticeBox.Paragraph>
          </SidebarNoticeBox>
        </Box>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="secondaryUtility"
          onClick={() => confirmDelete({
            callbacks: {
              deleteFn,
              afterAction: () => closeModal(),
            },
            strings: {
              confirmString: 'Are you sure? This is not reversable.',
              entityName: `${formattedType}`,
            },
          })}
          size="medium"
          style={{ marginRight: 8, width: 'calc(50% - 4px)' }}
        >
          Delete Only
        </Button>

        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating}
          // @ts-expect-error TS2345
          onClick={() => confirmAction(createCreditNotePayload)}
          size="medium"
          style={{ width: 'calc(50% - 4px)' }}
        >
          Create Credit Note
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateCreditNoteAndDeleteOrderModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
